import { useState, useEffect, useRef, useCallback } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useChannels } from "@/hooks/useChannels";
import { StatusBadge, BillingBadge, timeAgo, formatDate } from "@/components/shared";
import { Search, Send, Check, CheckCheck, Clock, AlertCircle, UserPlus, Phone, Radio, MessagesSquare, FileText, Image, Video, MapPin, Contact, Smile, StickyNote, ExternalLink } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const StatusIcon = ({ status }) => {
  if (status === "pending") return <Clock className="w-3.5 h-3.5 text-neutral-400" />;
  if (status === "sent") return <Check className="w-3.5 h-3.5 text-neutral-400" />;
  if (status === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-neutral-400" />;
  if (status === "read") return <CheckCheck className="w-3.5 h-3.5 text-[#003399]" />;
  if (status === "failed") return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  return null;
};

const MessageContent = ({ message_type, content }) => {
  if (!content) return <p className="text-sm text-neutral-500 italic">[{message_type}]</p>;

  switch (message_type) {
    case "text":
      return <p className="text-sm text-neutral-800 whitespace-pre-wrap break-words">{content.text?.body || ""}</p>;

    case "image":
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-neutral-700"><Image className="w-4 h-4" /> Gambar</div>
          {content.image?.caption && <p className="text-sm text-neutral-600">{content.image.caption}</p>}
        </div>
      );

    case "video":
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-neutral-700"><Video className="w-4 h-4" /> Video</div>
          {content.video?.caption && <p className="text-sm text-neutral-600">{content.video.caption}</p>}
        </div>
      );

    case "audio":
      return <div className="flex items-center gap-2 text-sm text-neutral-700"><Phone className="w-4 h-4" /> Pesan suara</div>;

    case "document":
      return (
        <div className="flex items-center gap-2 text-sm text-neutral-700">
          <FileText className="w-4 h-4" />
          {content.document?.filename || "Dokumen"}
        </div>
      );

    case "sticker":
      return <div className="flex items-center gap-2 text-sm text-neutral-700"><StickyNote className="w-4 h-4" /> Sticker</div>;

    case "location":
      return (
        <div className="flex items-center gap-2 text-sm text-neutral-700">
          <MapPin className="w-4 h-4" />
          {content.location?.name || "Lokasi"}
        </div>
      );

    case "contacts":
      return <div className="flex items-center gap-2 text-sm text-neutral-700"><Contact className="w-4 h-4" /> Kontak dibagikan</div>;

    case "reaction":
      return <div className="flex items-center gap-2 text-sm"><Smile className="w-4 h-4" /> {content.reaction?.emoji || "Reaction"}</div>;

    case "template":
      return <TemplatePreview content={content} />;

    case "interactive":
      return <InteractivePreview content={content} />;

    default:
      return <p className="text-sm text-neutral-500 italic">[{message_type}]</p>;
  }
};

const TemplatePreview = ({ content }) => {
  const tpl = content?.template;
  if (!tpl) return <p className="text-sm text-neutral-500 italic">[template]</p>;

  const components = tpl.components || [];
  const body = components.find(c => c.type === "body");
  const buttons = components.find(c => c.type === "button")?.buttons || components.find(c => c.type === "BUTTONS")?.buttons || [];

  return (
    <div className="space-y-2">
      <p className="text-sm text-neutral-800">{body?.text || tpl.name}</p>
      {buttons.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {buttons.map((b, i) => (
            <span key={i} className="text-xs border border-[#003399] text-[#003399] rounded-md px-2 py-1">
              {b.text || b.type}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const InteractivePreview = ({ content }) => {
  const interactive = content?.interactive;
  if (!interactive) return <p className="text-sm text-neutral-500 italic">[interactive]</p>;

  const body = interactive.body?.text || "";
  const buttons = interactive.action?.buttons || [];
  const sections = interactive.action?.sections || [];

  return (
    <div className="space-y-2">
      {body && <p className="text-sm text-neutral-800">{body}</p>}
      {buttons.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {buttons.map((b, i) => (
            <span key={i} className="text-xs border border-[#003399] text-[#003399] rounded-md px-2 py-1">
              {b.reply?.title || b.text || "Button"}
            </span>
          ))}
        </div>
      )}
      {sections.length > 0 && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> {interactive.action?.button || "Lihat opsi"}
        </div>
      )}
    </div>
  );
};

export default function Conversations() {
  const channels = useChannels();
  const [channelFilter, setChannelFilter] = useState("all");
  const [q, setQ] = useState("");
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [compose, setCompose] = useState("");
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const timelineRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const params = { limit: 50, q };
    if (channelFilter !== "all") params.channel_id = channelFilter;
    const res = await api.get("/conversations", params);
    setConvos(res.data || []);
  }, [q, channelFilter]);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  const openConvo = async (c) => {
    setActive(c);
    const [msgs, det] = await Promise.all([
      api.get(`/conversations/${c.id}/messages`),
      api.get(`/conversations/${c.id}`),
    ]);
    setMessages(msgs.data || []);
    setDetail(det.data);
    const tpl = await api.get("/templates/approved", { channel_id: c.channel_id });
    setTemplates(tpl.data || []);
  };

  useEffect(() => {
    if (timelineRef.current) timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [messages]);

  const send = async (content, template_id = null) => {
    if (!active) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.id}/messages`, { message_type: template_id ? "template" : "text", content, template_id });
      setCompose("");
      const msgs = await api.get(`/conversations/${active.id}/messages`);
      setMessages(msgs.data || []);
      loadConvos();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const saveContact = async () => {
    try {
      await api.post(`/conversations/${active.id}/save-contact`, { name: active.participant_name });
      toast.success("Kontak berhasil disimpan.");
      openConvo(active);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const channelName = (id) => channels.find((c) => c.id === id)?.name || "";

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] lg:grid-cols-[320px_1fr_300px] h-[calc(100vh-4rem)] overflow-hidden bg-white">
      {/* Chat list */}
      <div className="border-r border-[#ECECEC] bg-white flex flex-col overflow-hidden">
        <div className="p-3 border-b border-[#ECECEC] space-y-2 flex-shrink-0">
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="bg-white" data-testid="conversation-channel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua channel</SelectItem>
              {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari percakapan..." data-testid="conversation-search"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-transparent text-sm bg-[#F0F2F5] focus:outline-none focus:ring-2 focus:ring-[#003399]" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convos.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">Tidak ada percakapan.</p>}
          {convos.map((c) => (
            <button key={c.id} onClick={() => openConvo(c)} data-testid={`conversation-item-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#F0F0F0] hover:bg-neutral-50 flex gap-3 items-center ${active?.id === c.id ? "bg-[#003399]/5" : ""}`}>
              <div className="w-11 h-11 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-semibold flex-shrink-0">
                {(c.participant_name || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center gap-2">
                  <p className="font-medium text-neutral-900 truncate text-sm">{c.participant_name || c.participant_phone_number}</p>
                  <span className="text-xs text-neutral-400 flex-shrink-0">{timeAgo(c.last_message_at)}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <p className="text-xs text-neutral-500 truncate">{c.last_message_preview || "—"}</p>
                  {c.unread_count > 0 && <span className="bg-[#FFDA1A] text-[#111111] font-bold text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 flex-shrink-0">{c.unread_count}</span>}
                </div>
                <p className="text-[10px] text-neutral-400 mt-0.5 truncate">{channelName(c.channel_id)}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 chat-bg">
            <div className="bg-white rounded-lg p-10 max-w-sm">
              <MessagesSquare className="w-14 h-14 text-neutral-400 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold text-neutral-800 mb-2">Pilih percakapan</h3>
              <p className="text-sm text-neutral-500">Pilih chat di sebelah kiri untuk melihat timeline pesan.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="h-16 border-b border-[#ECECEC] flex items-center px-4 gap-3 flex-shrink-0 bg-white">
              <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 font-semibold">
                {(active.participant_name || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-neutral-900 truncate">{active.participant_name || active.participant_phone_number}</p>
                <p className="text-xs text-neutral-500 font-mono">{active.participant_phone_number}</p>
              </div>
              {!active.contact_id && (
                <Button size="sm" variant="outline" onClick={saveContact} data-testid="save-contact-button"><UserPlus className="w-4 h-4 mr-1.5" /> Simpan Kontak</Button>
              )}
            </div>

            <div ref={timelineRef} className="flex-1 overflow-y-auto chat-bg p-4 space-y-2" data-testid="message-timeline">
              {messages.map((m) => {
                const out = m.direction === "outbound";
                return (
                  <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${out ? "bg-[#FFF3C2]" : "bg-white"}`}>
                      {m.template_name && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0A8A00] bg-[#0A8A0015] rounded-sm px-1.5 py-0.5 mb-1">
                          Template · {m.template_name}
                        </span>
                      )}
                      <MessageContent message_type={m.message_type} content={m.content} />
                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        {out && m.billing === "paid" && <span className="text-[9px] font-semibold uppercase tracking-wide text-[#003399]/60">Berbayar</span>}
                        <span className="text-[10px] text-neutral-400">{formatDate(m.created_at)}</span>
                        {out && <StatusIcon status={m.status} />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-white p-3 border-t border-[#ECECEC] flex-shrink-0">
              {templates.length > 0 && (
                <div className="mb-2">
                  <Select onValueChange={(id) => { const t = templates.find((x) => x.id === id); if (t) send({ text: (t.components?.find((c) => c.type === "BODY")?.text) || t.name }, t.id); }}>
                    <SelectTrigger className="bg-white h-8 text-xs w-56" data-testid="template-picker"><SelectValue placeholder="Kirim template approved..." /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <textarea value={compose} onChange={(e) => setCompose(e.target.value)} rows={1} placeholder="Ketik pesan (outbox internal)..." data-testid="compose-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (compose.trim()) send({ text: compose }); } }}
                  className="flex-1 resize-none rounded-full border border-transparent px-4 py-2.5 text-sm bg-[#F0F2F5] focus:outline-none focus:ring-2 focus:ring-[#003399] max-h-24" />
                <button onClick={() => compose.trim() && send({ text: compose })} disabled={sending || !compose.trim()} data-testid="compose-send"
                  className="w-10 h-10 rounded-full bg-[#003399] text-white flex items-center justify-center hover:bg-[#002B80] disabled:opacity-50 flex-shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail panel */}
      <div className="border-l border-[#ECECEC] bg-white overflow-y-auto hidden lg:flex flex-col">
        {detail ? (
          <div className="p-5 space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-neutral-200 flex items-center justify-center text-2xl font-semibold text-neutral-600 mx-auto mb-3">
                {(detail.participant_name || "?").charAt(0)}
              </div>
              <h3 className="font-heading text-lg font-bold text-neutral-900">{detail.participant_name || "Tanpa Nama"}</h3>
              <p className="text-sm text-neutral-500 font-mono flex items-center justify-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" /> {detail.participant_phone_number}</p>
            </div>
            <div className="border-t border-[#F0F0F0] pt-4 space-y-3">
              <DetailRow icon={Radio} label="Channel" value={detail.channel?.name} />
              <DetailRow icon={UserPlus} label="Kontak" value={detail.contact ? "Tersimpan" : "Belum disimpan"} />
              {detail.contact?.company && <DetailRow icon={FileText} label="Perusahaan" value={detail.contact.company} />}
              {detail.contact?.opt_in_status && (
                <div className="flex items-center gap-3 text-sm">
                  <UserPlus className="w-4 h-4 text-neutral-400" />
                  <span className="text-neutral-500 flex-1">Opt-in</span>
                  <StatusBadge status={detail.contact.opt_in_status} />
                </div>
              )}
            </div>
            {detail.contact?.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {detail.contact.tags.map((t) => <span key={t} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full border border-[#ECECEC]">{t}</span>)}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-neutral-400 p-6 text-center">Detail kontak akan tampil di sini.</div>
        )}
      </div>
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 text-sm">
    <Icon className="w-4 h-4 text-neutral-400" />
    <span className="text-neutral-500 flex-1">{label}</span>
    <span className="font-medium text-neutral-900 truncate">{value || "-"}</span>
  </div>
);
